import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ExpensesByCategoryData } from '../types';
import { formatCurrency } from '../utils/formatters';

interface CategoryBreakdownProps {
  categoryData: ExpensesByCategoryData | null;
  loading?: boolean;
}

const COLORS = [
  '#5B21B6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#14B8A6', '#84CC16', '#F97316', '#EC4899'
];


const ExpensesByCategoryBreakdown: React.FC<CategoryBreakdownProps> = ({ categoryData, loading = false }) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  if (!categoryData || loading) {
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );
  }

  if (!categoryData.expenses || categoryData.expenses.length === 0) {
    return (
      <div className="empty-categories-message">
        <div className="empty-icon">📋</div>
        <div>No categories to display</div>
      </div>
    );
  }

  // Show parent categories with their combined totals from backend
  const parentCategories = categoryData.expenses.filter(cat => cat.is_parent);
  const total = categoryData.total;

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  return (
    <div className="category-list">
      {parentCategories.map((parentCategory, index) => {
        const categoryTotal = typeof parentCategory.total === 'string' ? parseFloat(parentCategory.total) : parentCategory.total;
        const totalAmount = typeof total === 'string' ? parseFloat(total) : total;
        const percentage = totalAmount > 0 ? (categoryTotal / totalAmount) * 100 : 0;
        const isExpanded = expandedCategories.has(parentCategory.category);
        const subcategoriesForThisCategory = categoryData.expenses.filter(cat =>
          !cat.is_parent && cat.category.startsWith(parentCategory.category + '/')
        );
        const hasSubcategories = subcategoriesForThisCategory.length > 0;
        const currency = categoryData.currency || 'USD';

        return (
          <div key={parentCategory.category}>
            {/* Main Category */}
            <div
              className={`category-item ${hasSubcategories ? 'expandable' : ''}`}
              onClick={() => hasSubcategories && toggleCategory(parentCategory.category)}
            >
              <div className="category-info">
                <div
                  className="category-color"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <div className="category-text">
                  <div className="text-body">{parentCategory.category}</div>
                  <div className="text-light">{percentage.toFixed(1)}% of total</div>
                </div>
                {hasSubcategories && (
                  <div className="expand-icon">
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </div>
                )}
              </div>
              <div className="amount">
                {formatCurrency(parentCategory.total, currency)}
              </div>
            </div>

            {/* Subcategories */}
            {hasSubcategories && isExpanded && (
              <div className="subcategories">
                {subcategoriesForThisCategory
                  .sort((a, b) => b.total - a.total)
                  .map((subcategory, subIndex) => {
                    const subPercentage = parentCategory.total > 0 ? (subcategory.total / parentCategory.total) * 100 : 0;
                    const subcategoryName = subcategory.category.split('/')[1] || subcategory.category;

                    return (
                      <div key={subcategory.category} className="subcategory-item">
                        <div className="subcategory-info">
                          <div
                            className="subcategory-indicator"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <div className="category-text">
                            <div className="text-body">{subcategoryName}</div>
                            <div className="text-light">{subPercentage.toFixed(1)}% of {parentCategory.category}</div>
                          </div>
                        </div>
                        <div className="amount">
                          {formatCurrency(subcategory.total, currency)}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ExpensesByCategoryBreakdown;
